import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, Input, OnInit, inject, input, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { captureURLImage } from "@auvious/media-tools";
import { filter } from "rxjs/operators";

import {
  ConferenceService,
  debug,
  debugError,
  StreamState,
} from "../../services";
import { PointerService } from "../../services/pointer.service";

@Component({
  selector: "app-freeze-frame",
  templateUrl: "./freeze-frame.component.html",
  styleUrls: ["./freeze-frame.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FreezeFrameComponent implements OnInit, AfterViewInit {
  private conferenceService = inject(ConferenceService);
  private point = inject(PointerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly streamFrame = viewChild<ElementRef<HTMLImageElement>>("streamImg");
  readonly blackTestCanvas = viewChild<ElementRef<HTMLCanvasElement>>("testCanvas");

  readonly stream = input<StreamState>(undefined);
  readonly mirror = input<boolean>(undefined);
  readonly blackCheck = input(false);
  readonly blur = input(true);
  @Input()
  set render(value: boolean) {
    if (value) {
      this.extractFrame();
    }
  }

  // imageRef: HTMLImageElement;
  isZoomOn = false;

  private autoExtractEnabled = false;

  ngOnInit(): void {
    this.point.zoomFrameChange$
      .pipe(
        filter(
          (f) => {
            const stream = this.stream();
            return f.target.endpoint === stream.originator.endpoint &&
              f.correlationId === stream.correlationId;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((f) => {
        this.isZoomOn = f.on;
        this.cdr.detectChanges();
      });
  }

  ngAfterViewInit() {
    this.autoExtractFrame();
  }

  extractFrame() {
    try {
      const videoElm = this.conferenceService.getElementById(
        this.stream()?.id
      ) as HTMLVideoElement;

      if (
        videoElm &&
        videoElm.videoWidth &&
        videoElm.videoHeight &&
        !(this.blackCheck() && this.isBlack(videoElm))
      ) {
        this.streamFrame().nativeElement.setAttribute(
          "src",
          captureURLImage(videoElm, { mirror: this.mirror() })
        );

        this.cdr.markForCheck();
        debug("frame extracted");
      }
    } catch (ex) {
      debugError(ex);
    }
  }

  autoExtractFrame() {
    const videoElm = this.conferenceService.getElementById(
      this.stream()?.id
    ) as HTMLVideoElement;

    if (!this.autoExtractEnabled && videoElm) {
      videoElm.addEventListener("playing", () => {
        this.extractFrame();
      });

      this.autoExtractEnabled = true;
    }
  }

  /**
   * Draw the frame in a 1x1 canvas and check the rgb value.
   */
  isBlack(img) {
    try {
      const ctx = this.blackTestCanvas().nativeElement.getContext("2d");
      this.blackTestCanvas().nativeElement.width =
        this.blackTestCanvas().nativeElement.height = 1;
      ctx.drawImage(img, 0, 0, 1, 1);
      const result = ctx.getImageData(0, 0, 1, 1);
      return result.data[0] + result.data[1] + result.data[2] < 30;
    } catch (ex) {
      debugError(ex);
    }
  }
}
